"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Plus,
  LogOut,
  X,
  Globe,
  History,
  Files,
  Menu,
  PanelLeftClose,
  Search,
  Settings,
  BookOpen,
  type LucideIcon,
} from "lucide-react";
import { Button, Input } from "./ui";
import {
  WebsitePageDetails,
  type WebsitePageAction,
} from "./WebsitePageDetails";
import { WebsitePageMenu } from "./WebsitePageMenu";
import { WebsiteCreation } from "./WebsiteCreation";
import { WebsiteAgentConnections } from "./WebsiteAgentConnections";
import { WebsiteAIWallet } from "./WebsiteAIWallet";
import { WebsitePagePublishingSetup } from "./WebsitePagePublishingSetup";
import { WebsiteCustomerActivation } from "./WebsiteCustomerActivation";
import { WebsiteVersionHistory } from "./WebsiteVersionHistory";
import {
  platform,
  type DesignContent,
  type DesignPage,
  type DesignPageInventory,
  type DesignSession,
  type DesignWebsite,
} from "./api";
import { WorkspaceListFilter } from "./WorkspaceListFilter";
import { pageStatus, openWorkspaceRow } from "./workspace-list";
import { BUILDY } from "./brand";
import css from "./BuildyWorkspace.module.css";

type View = "pages" | "history" | "canvas" | "settings";
export interface BuildyWorkspaceProps {
  session: DesignSession;
  org: number | null;
  sites: DesignWebsite[];
  loading?: boolean;
  error?: string;
  busy?: boolean;
  canvasWebsiteId?: string;
  initialWebsiteId?: string;
  onWebsiteSelected?: (websiteId: string) => void;
  portfolio?: boolean;
  initialPage?: string;
  onOrg: (id: number) => void;
  onOpen: (site: DesignWebsite, page?: string) => void;
  onLogout: () => void;
  beforeNavigate?: () => boolean;
  renderCanvas?: (
    site: DesignWebsite,
    onBack: () => void,
    navigationControl: ReactNode,
    page: string,
    onHistory: () => void,
    onPageChange: (page: string) => void,
    onSettings?: () => void,
  ) => ReactNode;
  billing?: ReactNode;
  previewOnly?: boolean;
  hasUnsavedWork?: boolean;
  onWebsiteActivated?: (websiteId: string, organizationId: number) => void;
  onWebsiteUpdated?: (website: DesignWebsite) => void;
  onWebsiteCreated?: (website: DesignWebsite, organizationId: number) => void;
}

export function BuildyWorkspace({
  session,
  org,
  sites,
  loading = false,
  error,
  busy = false,
  canvasWebsiteId,
  initialWebsiteId,
  onWebsiteSelected,
  initialPage = "/",
  portfolio: portfolioOverride,
  onOrg,
  onOpen,
  onLogout,
  beforeNavigate = () => true,
  renderCanvas,
  billing,
  previewOnly = false,
  hasUnsavedWork = false,
  onWebsiteActivated,
  onWebsiteUpdated,
  onWebsiteCreated,
}: BuildyWorkspaceProps) {
  const websiteUpdate = useRef(onWebsiteUpdated);
  websiteUpdate.current = onWebsiteUpdated;
  const [view, setView] = useState<View>(canvasWebsiteId ? "canvas" : "pages");
  const [selected, setSelected] = useState(
    canvasWebsiteId ?? initialWebsiteId ?? "",
  );
  const [navigation, setNavigation] = useState(false);
  const [picker, setPicker] = useState(false);
  const [addingWebsite, setAddingWebsite] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const activeOrg = useRef(org);
  activeOrg.current = org;
  const accountRoot = useRef<HTMLDetailsElement>(null);
  const setupDialog = useRef<HTMLElement>(null);
  const [query, setQuery] = useState("");
  const [pageQuery, setPageQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const pageScroll = useRef<HTMLDivElement>(null);
  const [requestedPage, setRequestedPage] = useState(initialPage);
  const [pageList, setPageList] = useState<DesignPage[]>([]);
  const [inventory, setInventory] = useState<DesignPageInventory | null>(null);
  const [renaming, setRenaming] = useState<{
    page: DesignPage;
    action: WebsitePageAction;
  } | null>(null);
  const [pagesRefresh, setPagesRefresh] = useState(0);
  const registry = session.page_inventory === true && !previewOnly;
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState("");
  const pickerRoot = useRef<HTMLDivElement>(null);
  const pickerTrigger = useRef<HTMLButtonElement>(null);
  const [pickerBounds, setPickerBounds] = useState({
    left: 16,
    top: 0,
    width: 320,
    maxHeight: 330,
  });
  useLayoutEffect(() => {
    if (!picker) return;
    const position = () => {
      const trigger = pickerTrigger.current?.getBoundingClientRect();
      if (!trigger) return;
      const width = Math.min(320, window.innerWidth - 32);
      const top = trigger.bottom + 6;
      setPickerBounds({
        left: Math.max(
          16,
          Math.min(trigger.left, window.innerWidth - width - 16),
        ),
        top,
        width,
        maxHeight: Math.max(80, Math.min(330, window.innerHeight - top - 16)),
      });
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [picker]);
  const navTrigger = useRef<HTMLButtonElement>(null);
  const navRoot = useRef<HTMLElement>(null);
  const historyReturnFocus = useRef<HTMLElement | null>(null);
  const editorRoot = useRef<HTMLDivElement>(null);
  const organization = session.organizations.find((item) => item.id === org);
  const portfolio = portfolioOverride ?? organization?.is_agency === true;
  useEffect(() => {
    setCanCreate(false);
    if (previewOnly || org === null || !onWebsiteCreated) return;
    const controller = new AbortController();
    void platform<{ can_create: boolean }>("websites/capabilities/", {
      org,
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setCanCreate(value.can_create === true);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [org, previewOnly, onWebsiteCreated, sites.length]);
  const site =
    sites.find((item) => item.id === selected) ??
    (canvasWebsiteId ? undefined : sites[0]);
  const focused = view === "canvas";
  const searchedPages = pageList.filter((page) =>
    `${page.name} ${page.path}`
      .toLocaleLowerCase()
      .includes(pageQuery.trim().toLocaleLowerCase()),
  );
  const shownPages = searchedPages.filter(
    (page) => statusFilter === "All" || pageStatus(page) === statusFilter,
  );
  const statusOptions = [
    "All",
    "Published",
    "Unpublished changes",
    "Draft",
    "Removal pending",
  ].map((label) => ({
    value: label,
    label,
    count:
      label === "All"
        ? searchedPages.length
        : searchedPages.filter((page) => pageStatus(page) === label).length,
  }));
  useEffect(() => {
    if (pageScroll.current) pageScroll.current.scrollTop = 0;
  }, [pageQuery, statusFilter]);
  useEffect(() => {
    if (
      view !== "settings" ||
      !site ||
      org === null ||
      previewOnly ||
      !websiteUpdate.current
    )
      return;
    const controller = new AbortController();
    void platform<DesignWebsite>(`websites/${site.id}/`, {
      org,
      signal: controller.signal,
    })
      .then((website) => {
        if (!controller.signal.aborted) websiteUpdate.current?.(website);
      })
      .catch(() => {
        /* Actions still revalidate authority and release on the server. */
      });
    return () => controller.abort();
  }, [view, site?.id, org, previewOnly]);
  useEffect(() => {
    setPageQuery("");
    setStatusFilter("All");
    setRenaming(null);
  }, [org, site?.id]);
  const shownSites = sites.filter((item) =>
    item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );
  useEffect(() => {
    setSelected(canvasWebsiteId ?? initialWebsiteId ?? "");
    setRequestedPage(initialPage);
    setView(canvasWebsiteId ? "canvas" : "pages");
    setNavigation(false);
  }, [org, canvasWebsiteId, initialPage, initialWebsiteId]);
  useEffect(() => {
    if (view !== "pages" || !site || org === null) return;
    const controller = new AbortController();
    setPagesLoading(true);
    setPagesError("");
    setPageList([]);
    let timer: ReturnType<typeof setTimeout>;
    async function load() {
      try {
        if (registry) {
          const result = await platform<DesignPageInventory>(
            `websites/${site!.id}/pages/`,
            { org: org!, signal: controller.signal },
          );
          if (controller.signal.aborted) return;
          setInventory(result);
          setPageList(result.pages);
        } else {
          const content = await platform<DesignContent>(
            `websites/${site!.id}/content/`,
            { org: org!, signal: controller.signal },
          );
          if (controller.signal.aborted) return;
          const paths =
            content.manifest?.pages ??
            content.manifest?.fields.map((field) => field.page) ??
            [];
          setPageList(
            [
              ...new Set(
                paths.filter((path) => /^\/(?:[A-Za-z0-9_-]+\/)*$/.test(path)),
              ),
            ]
              .sort((a, b) =>
                a === "/" ? -1 : b === "/" ? 1 : a.localeCompare(b),
              )
              .map((path) => ({
                id: path,
                path,
                name: pageName(path),
                status: "draft",
                source_declared: false,
                publication_intent: true,
                in_draft: true,
                has_unpublished_changes: false,
                removal_pending: false,
              })),
          );
        }
        setPagesError("");
      } catch (cause) {
        if (!controller.signal.aborted)
          setPagesError(
            cause instanceof Error
              ? cause.message
              : "Pages could not be loaded.",
          );
      } finally {
        if (!controller.signal.aborted) {
          setPagesLoading(false);
          if (registry) timer = setTimeout(() => void load(), 4000);
        }
      }
    }
    setInventory(null);
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [view, org, site?.id, site?.source_version, registry, pagesRefresh]);

  useEffect(() => {
    if (!navigation) return;
    const root = navRoot.current;
    root?.querySelector<HTMLButtonElement>("button")?.focus();
    function trap(event: KeyboardEvent) {
      if (event.key !== "Tab" || !root) return;
      const controls = [
        ...root.querySelectorAll<HTMLElement>(
          "button:not(:disabled),a[href],summary,input",
        ),
      ].filter((node) => node.getClientRects().length);
      const first = controls[0],
        last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", trap);
    return () => document.removeEventListener("keydown", trap);
  }, [navigation]);
  useEffect(() => {
    if (!picker) return;
    const selectedChoice =
      pickerRoot.current?.querySelector<HTMLButtonElement>(
        '[data-site-choice][aria-checked="true"]',
      ) ??
      pickerRoot.current?.querySelector<HTMLButtonElement>(
        '[aria-checked="true"], [data-context-choice]',
      );
    selectedChoice?.focus();
    const outside = (event: PointerEvent) => {
      if (!pickerRoot.current?.contains(event.target as Node)) setPicker(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [picker]);
  useEffect(() => {
    function outside(event: PointerEvent) {
      if (
        accountRoot.current &&
        !accountRoot.current.contains(event.target as Node)
      )
        accountRoot.current.open = false;
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape" && accountRoot.current?.open) {
        accountRoot.current.open = false;
        accountRoot.current.querySelector("summary")?.focus();
        event.preventDefault();
        event.stopPropagation();
      }
    }
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape, true);
    };
  }, []);
  useEffect(() => {
    if (!addingWebsite) return;
    (
      setupDialog.current?.querySelector<HTMLElement>("input:not(:disabled)") ??
      setupDialog.current?.querySelector<HTMLElement>("button:not(:disabled)")
    )?.focus();
  }, [addingWebsite]);
  function closeSetup() {
    setAddingWebsite(false);
    pickerTrigger.current?.focus();
  }
  useEffect(() => {
    if (focused && historyReturnFocus.current?.isConnected) {
      historyReturnFocus.current.focus();
      historyReturnFocus.current = null;
    }
  }, [focused]);
  function go(next: View) {
    if (next === "history" && focused) {
      const activeElement = document.activeElement as HTMLElement;
      historyReturnFocus.current = editorRoot.current?.contains(activeElement)
        ? activeElement
        : (editorRoot.current?.querySelector<HTMLElement>("button") ?? null);
    }
    setView(next);
    setNavigation(false);
  }
  function open(item: DesignWebsite, page = "/") {
    if (item.id === canvasWebsiteId && page === requestedPage) {
      setView("canvas");
      setNavigation(false);
      return;
    }
    if (!beforeNavigate()) return;
    setSelected(item.id);
    setRequestedPage(page);
    setPicker(false);
    setNavigation(false);
    if (renderCanvas && item.id === canvasWebsiteId) setView("canvas");
    else onOpen(item, page);
  }
  function closePicker() {
    setPicker(false);
    pickerTrigger.current?.focus();
  }
  const nav: [View, string, LucideIcon][] = [
    ["pages", "Pages", Files],
    ["history", "Version history", History],
  ];
  return (
    <div
      className={`${css.shell} ${focused ? css.focused : ""} ${navigation ? css.navigationOpen : ""}`}
    >
      <aside
        ref={navRoot}
        className={css.sidebar}
        role={navigation ? "dialog" : undefined}
        aria-modal={navigation ? true : undefined}
        aria-label="Buildy navigation"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setNavigation(false);
            navTrigger.current?.focus();
          }
        }}
      >
        <div className={css.brandRow}>
          <button
            className={css.brand}
            onClick={() => go("pages")}
            aria-label="Buildy pages"
          >
            <img
              className={css.brandRobot}
              src={BUILDY.robot}
              alt=""
              width={44}
              height={54}
            />
            <span className={css.brandWordmark}>{BUILDY.name}</span>
          </button>
          <button
            className={css.closeNav}
            aria-label="Close navigation"
            onClick={() => {
              setNavigation(false);
              navTrigger.current?.focus();
            }}
          >
            <PanelLeftClose size={19} />
          </button>
        </div>
        <div className={css.context} ref={pickerRoot}>
          <button
            className={css.contextTrigger}
            ref={pickerTrigger}
            aria-label={`Select website: ${site?.name ?? "Select website"}`}
            title={site?.name ?? "Select website"}
            aria-expanded={picker}
            aria-haspopup="menu"
            onKeyDown={(event) => {
              if (["ArrowDown", "ArrowUp"].includes(event.key)) {
                event.preventDefault();
                setPicker(true);
              }
            }}
            aria-controls="buildy-context"
            disabled={busy}
            onClick={() => setPicker((value) => !value)}
          >
            <span className={css.contextIcon}>
              <Globe size={20} />
            </span>
            <span className={css.contextName}>
              {site?.name ?? "Select website"}
            </span>
            <ChevronsUpDown size={15} />
          </button>
          {picker && (
            <div
              id="buildy-context"
              className={css.contextPanel}
              style={pickerBounds}
              role="menu"
              aria-label="Websites"
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  closePicker();
                } else if (event.key === "Tab") {
                  setPicker(false);
                } else if (
                  ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
                ) {
                  if (event.target instanceof HTMLInputElement) return;
                  event.preventDefault();
                  const choices = [
                    ...event.currentTarget.querySelectorAll<HTMLElement>(
                      '[role^="menuitem"]',
                    ),
                  ];
                  const index = choices.indexOf(
                    document.activeElement as HTMLElement,
                  );
                  const next =
                    event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? choices.length - 1
                        : (index +
                            (event.key === "ArrowDown" ? 1 : -1) +
                            choices.length) %
                          choices.length;
                  choices[next]?.focus();
                } else if (
                  event.key.length === 1 &&
                  !event.ctrlKey &&
                  !event.metaKey &&
                  event.key !== " " &&
                  !(event.target instanceof HTMLInputElement)
                ) {
                  const choices = [
                    ...event.currentTarget.querySelectorAll<HTMLElement>(
                      '[role^="menuitem"]',
                    ),
                  ];
                  const index = choices.indexOf(
                    document.activeElement as HTMLElement,
                  );
                  const match = [
                    ...choices.slice(index + 1),
                    ...choices.slice(0, index + 1),
                  ].find((item) =>
                    item.textContent
                      ?.trim()
                      .toLowerCase()
                      .startsWith(event.key.toLowerCase()),
                  );
                  if (match) {
                    event.preventDefault();
                    match.focus();
                  }
                }
              }}
            >
              {!portfolio && session.organizations.length > 1 && (
                <>
                  {session.organizations.map((item) => (
                    <button
                      data-context-choice
                      role="menuitemradio"
                      tabIndex={-1}
                      key={item.id}
                      aria-checked={org === item.id}
                      onClick={() => {
                        if (item.id !== org && !beforeNavigate()) return;
                        closePicker();
                        if (item.id !== org) onOrg(item.id);
                      }}
                    >
                      <span>{item.name}</span>
                      {item.id === org && <Check size={15} />}
                    </button>
                  ))}
                  <div className={css.menuDivider} role="separator" />
                </>
              )}
              {sites.length > 0 && (
                <>
                  {sites.length > 5 && (
                    <label
                      className={css.search}
                      onPointerDown={(event) => {
                        event.currentTarget.dataset.pointerFocus = "true";
                      }}
                      onBlur={(event) => {
                        delete event.currentTarget.dataset.pointerFocus;
                      }}
                    >
                      <Search size={17} />
                      <Input
                        aria-label="Search websites"
                        placeholder="Search websites…"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    </label>
                  )}
                  {shownSites.map((item) => (
                    <button
                      data-context-choice
                      data-site-choice
                      role="menuitemradio"
                      tabIndex={-1}
                      aria-checked={site?.id === item.id}
                      key={item.id}
                      onClick={() => {
                        if (!beforeNavigate()) return;
                        setSelected(item.id);
                        onWebsiteSelected?.(item.id);
                        closePicker();
                        setView("pages");
                        setNavigation(false);
                      }}
                    >
                      <span>{item.name}</span>
                      {site?.id === item.id && <Check size={15} />}
                    </button>
                  ))}
                  {!shownSites.length && <p>No matching websites.</p>}
                </>
              )}
              {!sites.length && (
                <p>
                  {loading ? "Loading websites…" : "No saved websites yet."}
                </p>
              )}
              {(previewOnly || canCreate) && (
                <>
                  <div className={css.menuDivider} role="separator" />
                  <button
                    role="menuitem"
                    tabIndex={-1}
                    className={css.addWebsite}
                    onClick={() => {
                      if (!beforeNavigate()) return;
                      setPicker(false);
                      setAddingWebsite(true);
                    }}
                  >
                    <Plus size={16} />
                    Add website
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <nav className={css.nav} aria-label="Main navigation">
          {nav.map(([id, label, Icon]) => (
            <button
              key={id}
              aria-current={view === id ? "page" : undefined}
              onClick={() => go(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className={css.sidebarBottom}>
          <button
            aria-current={view === "settings" ? "page" : undefined}
            onClick={() => go("settings")}
          >
            <Settings size={18} />
            Settings
          </button>
          <details className={css.account} ref={accountRoot}>
            <summary aria-label={`Account menu for ${session.user.email}`}>
              <span className={css.avatar}>
                {session.user.email.slice(0, 1).toUpperCase()}
              </span>
              <span className={css.accountPerson}>
                <strong>{session.user.email.split("@")[0]}</strong>
                <span>
                  {site?.name ?? organization?.name ?? "Your websites"}
                </span>
              </span>
              <ChevronDown size={14} />
            </summary>
            <div className={css.accountPopover}>
              <button
                disabled={busy}
                onClick={() => {
                  if (beforeNavigate()) onLogout();
                }}
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </details>
        </div>
      </aside>
      {navigation && (
        <button
          className={css.scrim}
          aria-label="Close navigation overlay"
          onClick={() => {
            setNavigation(false);
            navTrigger.current?.focus();
          }}
        />
      )}
      {addingWebsite && (
        <div
          className={css.setupOverlay}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeSetup();
          }}
        >
          <section
            ref={setupDialog}
            className={css.setupDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="buildy-add-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                closeSetup();
              }
              if (event.key === "Tab") {
                const controls = [
                  ...event.currentTarget.querySelectorAll<HTMLElement>(
                    "button:not(:disabled),input:not(:disabled)",
                  ),
                ];
                const first = controls[0],
                  last = controls.at(-1);
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault();
                  last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault();
                  first?.focus();
                }
                event.stopPropagation();
              }
            }}
          >
            <button
              className={css.setupClose}
              aria-label="Close website setup"
              onClick={closeSetup}
            >
              <X size={20} />
            </button>
            <h2 id="buildy-add-title">Add website</h2>
            {!previewOnly && org !== null && onWebsiteCreated ? (
              <WebsiteCreation
                key={org}
                org={org}
                csrf={session.csrf_token}
                onCreated={(website) => {
                  onWebsiteCreated(website, org);
                  if (activeOrg.current !== org) return;
                  setSelected(website.id);
                  setView("pages");
                  setNavigation(false);
                  closeSetup();
                }}
              />
            ) : (
              <>
                <p>Start with the business and its current website.</p>
                <label>
                  Website name
                  <Input
                    autoComplete="organization"
                    placeholder="Business name"
                  />
                </label>
                <label>
                  Website address
                  <Input type="url" placeholder="https://example.com" />
                </label>
                <p className={css.setupNotice}>
                  Website creation is not connected in this local preview.
                </p>
                <Button disabled>Continue</Button>
              </>
            )}
          </section>
        </div>
      )}
      <div className={css.main}>
        {focused && !site && (
          <section className={css.content} role={loading ? "status" : "alert"}>
            <h1>
              {loading
                ? "Opening website…"
                : error
                  ? "Could not open website"
                  : "Website unavailable"}
            </h1>
            {!loading && (
              <>
                <p>
                  {error ||
                    "Choose an available website from Pages to continue."}
                </p>
                <Button
                  onClick={() => {
                    if (!beforeNavigate()) return;
                    if (org !== null) onOrg(org);
                    else go("pages");
                  }}
                >
                  <ArrowLeft size={16} /> Back to Pages
                </Button>
              </>
            )}
          </section>
        )}
        {!focused && (
          <div className={css.mobileBar}>
            <button
              ref={navTrigger}
              aria-label="Open navigation"
              aria-expanded={navigation}
              onClick={() => setNavigation(true)}
            >
              <Menu size={20} />
            </button>
            <span>{BUILDY.name}</span>
          </div>
        )}
        {site && canvasWebsiteId && renderCanvas && (
          <div ref={editorRoot} className={css.canvas} hidden={!focused}>
            {renderCanvas(
              site,
              () => go("pages"),
              <Button
                aria-label="Back to Pages"
                title="Back to Pages"
                onClick={() => {
                  if (beforeNavigate()) go("pages");
                }}
              >
                <ArrowLeft size={17} />
                Pages
              </Button>,
              requestedPage,
              () => go("history"),
              (page) => open(site, page),
              () => go("settings"),
            )}
          </div>
        )}
        {!focused && (
          <main
            className={`${css.content} ${view !== "settings" ? css.listContent : ""}`}
          >
            <header className={css.pageHeading}>
              <div>
                <h1>
                  {view === "settings"
                    ? "Settings"
                    : view === "history"
                      ? "Version history"
                      : "Pages"}
                </h1>
              </div>
            </header>
            {error && (
              <p role="alert" className={css.error}>
                {error}
              </p>
            )}
            {loading ? (
              <section className={css.panel} role="status">
                Loading your websites…
              </section>
            ) : view === "settings" ? (
              <div className={css.settingsSections}>
                <section className={css.panel}>
                  <div className={css.panelHeading}>
                    <h2>Account</h2>
                  </div>
                  <div className={css.panelBody}>
                    <dl className={css.details}>
                      <div>
                        <dt>Email</dt>
                        <dd>{session.user.email}</dd>
                      </div>
                      <div>
                        <dt>Organization</dt>
                        <dd>{organization?.name ?? "Not selected"}</dd>
                      </div>
                      <div>
                        <dt>Editing</dt>
                        <dd>
                          {site?.hosting?.editing_enabled === false
                            ? "Paused"
                            : "Available"}
                        </dd>
                      </div>
                      <div>
                        <dt>Access</dt>
                        <dd>
                          {organization?.can_edit
                            ? "Can edit websites"
                            : "View only"}
                        </dd>
                      </div>
                    </dl>
                    {session.guidance?.can_view && (
                      <a
                        className={css.textLink}
                        href="/playbook"
                        onClick={(event) => {
                          if (!beforeNavigate()) event.preventDefault();
                        }}
                      >
                        <BookOpen size={16} />
                        Website playbook
                      </a>
                    )}
                  </div>
                </section>
                {site &&
                  org !== null &&
                  organization?.can_activate &&
                  !previewOnly && (
                    <WebsiteCustomerActivation
                      key={site.id}
                      site={site}
                      org={org}
                      csrf={session.csrf_token}
                      onActivated={(organizationId) =>
                        onWebsiteActivated?.(site.id, organizationId)
                      }
                    />
                  )}
                {site &&
                  org !== null &&
                  organization?.can_activate &&
                  !previewOnly && (
                    <WebsitePagePublishingSetup
                      key={`prepare:${site.id}`}
                      websiteId={site.id}
                      org={org}
                      csrf={session.csrf_token}
                      onPrepared={() => open(site)}
                    />
                  )}
                {site && org !== null && !previewOnly && session.ai_wallet && (
                  <WebsiteAIWallet
                    key={`${org}:${site.id}`}
                    websiteId={site.id}
                    org={org}
                    csrf={session.csrf_token}
                  />
                )}
                {site &&
                  org !== null &&
                  !previewOnly &&
                  session.agent_connections && (
                    <WebsiteAgentConnections
                      key={`agent:${org}:${site.id}`}
                      websiteId={site.id}
                      org={org}
                      session={session}
                      canEdit={organization?.can_edit === true}
                    />
                  )}
                {billing}
              </div>
            ) : view === "history" ? (
              site && org !== null ? (
                <WebsiteVersionHistory
                  websiteId={site.id}
                  org={org}
                  previewOnly={previewOnly}
                  hasUnsavedWork={hasUnsavedWork}
                  canRestore={
                    !previewOnly &&
                    session.save_flow === true &&
                    session.organizations.some(
                      (item) => item.id === org && item.can_publish,
                    )
                  }
                  csrf={session.csrf_token}
                  pageName={pageName(requestedPage)}
                  onReturnEditor={
                    canvasWebsiteId && renderCanvas
                      ? () => go("canvas")
                      : undefined
                  }
                />
              ) : (
                <p className={css.pagesEmpty}>
                  Select a website to view its history.
                </p>
              )
            ) : (
              <>
                <div className={css.pagesToolbar}>
                  <label
                    className={css.search}
                    onPointerDown={(event) => {
                      event.currentTarget.dataset.pointerFocus = "true";
                    }}
                    onBlur={(event) => {
                      delete event.currentTarget.dataset.pointerFocus;
                    }}
                  >
                    <Search size={17} />
                    <Input
                      aria-label="Search pages"
                      placeholder="Search pages…"
                      value={pageQuery}
                      onChange={(event) => setPageQuery(event.target.value)}
                    />
                  </label>
                  {registry && (
                    <WorkspaceListFilter
                      label="Status"
                      value={statusFilter}
                      options={statusOptions}
                      onChange={setStatusFilter}
                    />
                  )}
                  <button
                    className={css.filterReset}
                    type="button"
                    style={{
                      visibility:
                        pageQuery || statusFilter !== "All"
                          ? "visible"
                          : "hidden",
                    }}
                    disabled={!pageQuery && statusFilter === "All"}
                    onClick={() => {
                      setPageQuery("");
                      setStatusFilter("All");
                    }}
                  >
                    Reset
                  </button>
                </div>
                {inventory?.save &&
                  ["CHECKING", "PUBLISHING", "FAILED"].includes(
                    inventory.save.status,
                  ) && (
                    <p
                      role={
                        inventory.save.status === "FAILED" ? "alert" : "status"
                      }
                      className={css.pagesNotice}
                    >
                      {inventory.save.status === "FAILED"
                        ? "The last save could not finish. Open a page to review and retry it."
                        : inventory.save.status === "PUBLISHING"
                          ? "Publishing your changes…"
                          : "Checking your changes…"}
                    </p>
                  )}
                <section
                  className={`${css.panel} ${css.pagesPanel}`}
                  aria-label="Website pages"
                >
                  <div
                    className={css.pagesTableScroll}
                    ref={pageScroll}
                    role="region"
                    aria-label="Pages table"
                    tabIndex={0}
                  >
                    <table
                      className={`${css.pagesTable} ${!pageList.length ? css.emptyPagesTable : ""}`}
                      aria-label="Website pages"
                    >
                      <thead>
                        <tr>
                          <th scope="col">Name</th>
                          <th scope="col">Path</th>
                          {registry && <th scope="col">Status</th>}
                          <th
                            scope="col"
                            className={css.pageActions}
                            aria-label="Page actions"
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {pagesLoading ? (
                          <tr>
                            <td colSpan={registry ? 4 : 3}>
                              <p className={css.pagesEmpty} role="status">
                                Loading pages…
                              </p>
                            </td>
                          </tr>
                        ) : pagesError ? (
                          <tr>
                            <td colSpan={registry ? 4 : 3}>
                              <p className={css.pagesEmpty} role="alert">
                                {pagesError}
                              </p>
                              <Button
                                onClick={() =>
                                  setPagesRefresh((value) => value + 1)
                                }
                              >
                                Try again
                              </Button>
                            </td>
                          </tr>
                        ) : shownPages.length ? (
                          shownPages.map((page) => (
                            <tr
                              key={page.id}
                              className={
                                !busy && page.in_draft
                                  ? css.openableRow
                                  : undefined
                              }
                              onClick={(event) => {
                                if (!busy && page.in_draft && site)
                                  openWorkspaceRow(event, () =>
                                    open(site, page.path),
                                  );
                              }}
                            >
                              <th scope="row">
                                <button
                                  className={css.pageName}
                                  data-row-open
                                  disabled={busy || !page.in_draft}
                                  onClick={() => site && open(site, page.path)}
                                >
                                  {page.name}
                                </button>
                              </th>
                              <td className={css.pagePath}>{page.path}</td>
                              {registry && (
                                <td>
                                  <span className={css.pageStatus}>
                                    {pageStatus(page)}
                                  </span>
                                </td>
                              )}
                              <td className={css.pageActions}>
                                <div className={css.pageActionGroup}>
                                  <button
                                    className={css.pageEdit}
                                    disabled={busy || !page.in_draft}
                                    aria-label={`Edit ${page.name}: ${page.path}`}
                                    onClick={() =>
                                      site && open(site, page.path)
                                    }
                                  >
                                    Edit <ArrowRight size={13} />
                                  </button>
                                  {registry &&
                                    session.save_flow &&
                                    page.source_declared &&
                                    page.in_draft &&
                                    organization?.can_edit &&
                                    (!site?.live_release ||
                                      organization.can_publish) &&
                                    site?.can_write !== false && (
                                      <WebsitePageMenu
                                        name={page.name}
                                        included={
                                          page.publication_intent ?? true
                                        }
                                        canSetVisibility={
                                          !!inventory?.visibility_supported &&
                                          page.path !== "/"
                                        }
                                        launched={!!site?.live_release}
                                        disabled={
                                          busy ||
                                          ["CHECKING", "PUBLISHING"].includes(
                                            inventory?.save?.status ?? "",
                                          )
                                        }
                                        onAction={(action) =>
                                          setRenaming({ page, action })
                                        }
                                      />
                                    )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={registry ? 4 : 3}>
                              {pageList.length ? (
                                <p className={css.pagesEmpty}>
                                  No matching pages. Try another search or reset
                                  the filters.
                                </p>
                              ) : (
                                <div className={css.pagesBlank}>
                                  <h2>
                                    {!site
                                      ? "No website yet"
                                      : organization?.can_edit &&
                                          site.can_write !== false
                                        ? "Let’s build your website"
                                        : "No pages yet"}
                                  </h2>
                                  <p>
                                    {!site
                                      ? "Your website will appear here once setup is complete."
                                      : organization?.can_edit &&
                                          site.can_write !== false
                                        ? "Tell Buildy about your business to create your first page."
                                        : "Your website’s pages will appear here once they have been created."}
                                  </p>
                                  {site &&
                                    organization?.can_edit &&
                                    site.can_write !== false && (
                                      <Button
                                        variant="primary"
                                        onClick={() => open(site, "/")}
                                        disabled={busy}
                                      >
                                        Create your homepage
                                      </Button>
                                    )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {!pagesLoading && !pagesError && (
                    <footer className={css.pagesFooter}>
                      {shownPages.length === pageList.length
                        ? `${pageList.length} ${pageList.length === 1 ? "page" : "pages"}`
                        : `${shownPages.length} of ${pageList.length} pages`}
                    </footer>
                  )}
                </section>
              </>
            )}
          </main>
        )}
      </div>
      {renaming && site && inventory && org !== null && (
        <WebsitePageDetails
          page={renaming.page}
          action={renaming.action}
          site={site}
          inventory={inventory}
          org={org}
          session={session}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            setPagesRefresh((value) => value + 1);
          }}
        />
      )}
    </div>
  );
}

function pageName(path: string) {
  if (path === "/") return "Home";
  return path
    .split("/")
    .filter(Boolean)
    .at(-1)!
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
